/**
 * A message with its clicks marked on it.
 *
 * A ranked list of addresses answers "which link won" but not "where was it" —
 * and in a message with a button at the top, another halfway down and a logo
 * in the footer, where it was is most of the answer. Three links to the same
 * page are three different questions about the layout.
 *
 * So the counts go on the message itself, over the links they belong to. The
 * badge and the number beside it in the list are read from the same events, so
 * the two can never disagree.
 *
 * Nothing here changes what was sent. It reads the copy, adds an attribute the
 * preview's own stylesheet draws, and hands it back.
 */

/*
 * Enough of them to render a label. A link reading "Start Free &rarr;" must
 * not come out with the markup showing, and pulling in a whole entity table
 * for six characters of arrow would be a poor trade.
 */
const ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  rarr: '→', larr: '←', mdash: '—', ndash: '–', hellip: '…',
  lsquo: '\u2018', rsquo: '\u2019', ldquo: '\u201C', rdquo: '\u201D',
  middot: '·', bull: '•', check: '✓', times: '×'
};

const unescape = text => String(text || '').replace(
  /&(#x?[0-9a-f]+|\w+);/gi,
  (whole, body) => {
    if (body[0] !== '#') return ENTITIES[body.toLowerCase()] || whole;
    const code = body[1] === 'x' || body[1] === 'X'
      ? parseInt(body.slice(2), 16)
      : parseInt(body.slice(1), 10);
    return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
  }
);

/**
 * One address, in the form two spellings of it agree on.
 *
 * The scheme and the host are case-insensitive and a trailing slash is not a
 * different page; the path is neither of those things and is left alone.
 */
export function linkKey(url) {
  /*
   * Decoded first. The copy carries `&amp;` because that is how an href is
   * correctly written, and a recorded click carries `&` because that is what
   * the reader's browser actually asked for. They are one address, and keying
   * them apart put every link in the message under a heading of its own.
   */
  const raw = unescape(String(url || '')).trim();
  const parts = raw.match(/^(https?:\/\/)([^/?#]+)(.*)$/i);
  if (!parts) return raw;
  return `${parts[1].toLowerCase()}${parts[2].toLowerCase()}${parts[3]}`.replace(/\/+$/, '');
}

/** What a link says, for a list that has to be read rather than decoded. */
function labelOf(inner) {
  const text = unescape(String(inner).replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ').trim();
  if (text) return { label: text.slice(0, 70), image: false };

  // A picture with nothing written on it: the alt text is what it is for.
  const alt = (String(inner).match(/<img[^>]*\salt="([^"]*)"/i) || [])[1];
  return { label: unescape(alt || '').trim() || 'an image', image: true };
}

/**
 * Every link in the copy, in the order a reader meets them.
 *
 * The same address twice is two entries, because they are two places in the
 * message — which is the whole point of looking at it this way.
 */
export function linksIn(html) {
  const found = [];
  const anchors = /<a\b[^>]*\shref="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi;

  let hit = anchors.exec(String(html || ''));
  let at = 0;

  while (hit) {
    const url = unescape(hit[1]);
    if (/^https?:/i.test(url)) {
      found.push({ at: at++, url, key: linkKey(url), ...labelOf(hit[2]) });
    }
    hit = anchors.exec(String(html || ''));
  }

  return found;
}

/**
 * The copy with a click count hung on every link that earned one.
 *
 * An attribute rather than an element: the preview's stylesheet draws it from
 * `data-xy-hits`, so the message's own layout is untouched. Nothing is
 * inserted into it, and a link nobody clicked looks exactly as it was sent.
 */
export function markHits(html, hits) {
  return String(html || '').replace(
    /<a\b([^>]*)\shref="([^"]+)"/gi,
    (whole, before, url) => {
      const count = hits.get(linkKey(unescape(url)));
      return count ? `${whole} data-xy-hits="${count}"` : whole;
    }
  );
}

/** What the preview needs to draw the badges. */
export const HEAT_CSS = `
  a[data-xy-hits]{position:relative;outline:2px solid #E11D48;outline-offset:2px;
    border-radius:4px}
  a[data-xy-hits]::after{content:attr(data-xy-hits);position:absolute;
    top:-11px;right:-13px;z-index:2;
    min-width:14px;padding:2px 6px;border-radius:11px;
    background:#E11D48;color:#ffffff;
    font:700 11px/1.35 Arial,Helvetica,sans-serif;text-align:center;
    box-shadow:0 1px 4px rgba(0,0,0,.35)}
`;
